import { useState } from "react";
import CoffeeGridBoxes from "./CoffeeGridBoxes";
import CoffeeModal from "./CoffeeModal";
import { ArrowRight } from "lucide-react";

const ITEMS_PER_PAGE = 18;

export default function CoffeeComponent() {
  const [selectedBeans, setSelectedBeans] = useState(null);
  const [filterOrigin, setFilterOrigin] = useState("");
  const [filterRoaster, setFilterRoaster] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterRating, setFilterRating] = useState("");
  const [showFullLegend, setShowFullLegend] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = AllCoffeeList.filter((b) =>
    (!filterOrigin || b.originCountry === filterOrigin) &&
    (!filterRoaster || b.roasterCountry === filterRoaster) &&
    (!filterCategory || b.category === filterCategory) &&
    (!filterRating || String(b.rating) === filterRating)
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const originCountries = [...new Set(AllCoffeeList.map((b) => b.originCountry).filter((c) => c && c !== "Unknown"))].sort();
  const roasterCountries = [...new Set(AllCoffeeList.map((b) => b.roasterCountry).filter((c) => c && c !== "Unknown"))].sort();
  const categories = ["Home Brew", "Self Roast", "Cafe Visit"];

  const handleFilter = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const selectClass = "text-sm bg-card border border-border text-foreground rounded px-3 py-1.5 focus:outline-none focus:border-foreground/40";

  return (
    <div className="space-y-8">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <select className={selectClass} value={filterOrigin} onChange={handleFilter(setFilterOrigin)}>
          <option value="">All Origins</option>
          {originCountries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={selectClass} value={filterRoaster} onChange={handleFilter(setFilterRoaster)}>
          <option value="">All Roaster Countries</option>
          {roasterCountries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={selectClass} value={filterCategory} onChange={handleFilter(setFilterCategory)}>
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={selectClass} value={filterRating} onChange={handleFilter(setFilterRating)}>
          <option value="">All Ratings</option>
          {[6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10].map((r) => (
            <option key={r} value={r}>{r}/10</option>
          ))}
        </select>
        {(filterOrigin || filterRoaster || filterCategory || filterRating) && (
          <button
            className="text-xs text-muted hover:text-foreground transition-colors underline underline-offset-2"
            onClick={() => { setFilterOrigin(""); setFilterRoaster(""); setFilterCategory(""); setFilterRating(""); setCurrentPage(1); }}
          >
            Clear filters
          </button>
        )}
        <span className="text-xs text-muted ml-auto">{filtered.length} coffees</span>
      </div>

      {/* Category Descriptions */}
      <div className="space-y-3">
        {(!filterCategory || filterCategory === "Home Brew") && (
          <div className="border-l-2 border-border pl-4">
            <p className="text-sm font-medium text-foreground mb-1">Coffees I Have Brewed at Home</p>
            <p className="text-sm text-muted">All the coffees here are from roasteries in Indonesia, Singapore, or other places I've travelled to. I'm pretty intentional about what I spend on beans in Singapore — I usually wait for good deals or promotions before pulling the trigger. Whenever I'm back in Indonesia, I take the chance to pick up beans directly from local roasters, which often means better quality at a fraction of the price. I also occasionally get test-roast samples from friends in the scene, which is always a treat.</p>
          </div>
        )}
        {(!filterCategory || filterCategory === "Self Roast") && (
          <div className="border-l-2 border-border pl-4">
            <p className="text-sm font-medium text-foreground mb-1">Coffees I Have Roasted Myself</p>
            <p className="text-sm text-muted">I happened to get free Rwanda sample beans from Singapore Coffee Association. I roasted them with an Ikawa Roaster at Compound Coffee, $2 per 50g. During my first time, I asked my friend to help because I had no idea about coffee roasting.</p>
          </div>
        )}
        {(!filterCategory || filterCategory === "Cafe Visit") && (
          <div className="border-l-2 border-border pl-4">
            <p className="text-sm font-medium text-foreground mb-1">Coffee Beans from Cafe Hopping and Cupping Sessions</p>
            <p className="text-sm text-muted">I rarely drink coffee outside since I don't really want to spend $5–10 for a coffee, except if I have a voucher or am traveling. The beans here are those that left me good impressions.</p>
          </div>
        )}
        {!filterCategory && (
          <div className="border-l-2 border-border pl-4">
            <p className="text-sm font-medium text-foreground mb-1">
              <a
                href="https://indocoffee-carbon-credit.vercel.app/"
                target="_blank"
                rel="noreferrer"
                className="hover:text-muted transition-colors inline-flex items-center gap-1"
              >
                Indocoffee Carbon Credit Project <ArrowRight size={13} />
              </a>
            </p>
            <p className="text-sm text-muted">My passion project developing carbon credit initiatives for Indonesian coffee farms.</p>
          </div>
        )}
      </div>

      {/* Rating Disclaimer */}
      <div className="relative">
        <p className="text-xs text-muted italic mb-3">
          Ratings are based on my personal taste and my understanding of what makes a good coffee — not a professional cupping score. Specialty coffee graders use an 80–100 scale where anything above 80 qualifies as specialty. I'm not there yet, so take my numbers as honest personal impressions, not certified assessments.
        </p>
        <div className="border border-border rounded-lg p-4 bg-card space-y-2">
          <p className="text-xs font-medium text-foreground mb-3">My Rating Scale</p>
          {[
            { score: "10", label: "Blowing my mind away" },
            { score: "9.5", label: "Great competition-level or world class coffee" },
            { score: "9", label: "Great coffee — could be competition-grade, or an affordable bean pushed to its absolute best" },
          ].map(({ score, label }) => (
            <div key={score} className="flex gap-3 text-xs">
              <span className="text-foreground font-medium w-8 flex-shrink-0">{score}</span>
              <span className="text-muted">{label}</span>
            </div>
          ))}
          {showFullLegend ? (
            <>
              {[
                { score: "8.5", label: "Very good for everyday drinking — strikes the balance between great taste and affordability" },
                { score: "8", label: "Same as 8.5 but a notch lower" },
                { score: "7.5", label: "An ok specialty coffee — enjoyable but nothing that stands out" },
                { score: "6–6.5", label: "Specialty coffee that was a bit disappointing" },
              ].map(({ score, label }) => (
                <div key={score} className="flex gap-3 text-xs">
                  <span className="text-foreground font-medium w-8 flex-shrink-0">{score}</span>
                  <span className="text-muted">{label}</span>
                </div>
              ))}
              <button onClick={() => setShowFullLegend(false)} className="text-xs text-muted hover:text-foreground underline underline-offset-2 transition-colors pt-1">
                Show less
              </button>
            </>
          ) : (
            <button onClick={() => setShowFullLegend(true)} className="text-xs text-muted hover:text-foreground underline underline-offset-2 transition-colors pt-1">
              View more ratings...
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center space-y-3">
          <p className="text-muted">No coffees match your current filters.</p>
          <button
            className="text-sm text-foreground underline underline-offset-2 hover:text-muted transition-colors"
            onClick={() => { setFilterOrigin(""); setFilterRoaster(""); setFilterCategory(""); setFilterRating(""); setCurrentPage(1); }}
          >
            Show all coffees
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {paginated.map((beans, index) => (
            <CoffeeGridBoxes key={index} beans={beans} onClick={setSelectedBeans} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-4">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="text-sm text-muted hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            ← Prev
          </button>
          <span className="text-sm text-muted">
            {currentPage} / {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="text-sm text-muted hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        </div>
      )}

      {/* Modal */}
      <CoffeeModal beans={selectedBeans} onClose={() => setSelectedBeans(null)} />
    </div>
  );
}

const AllCoffeeList = [
  // ─── Home Brew ────────────────────────────────────────────────────────────
  {
    beansName: "Philippine Deuce Sitio San Roque",
    roaster: "Deuce Coffee, Philippines",
    altitude: "",
    varietal: "Typica",
    process: "Natural",
    tastingnotes: "Pear, Nuts, Brown Sugar",
    personalremarks: "I went to Manila, Philippines, and brought this back home.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/PhilippineDeuce.jpg",
    originCountry: "Philippines", roasterCountry: "Philippines", rating: 6, category: "Home Brew",
  },
  {
    beansName: "Aceh Gayo",
    roaster: "Viel Coffee, Jakarta",
    altitude: "1300",
    varietal: "Mixed",
    process: "Anaerobic Natural",
    tastingnotes: "Apple, Lemon, Smooth",
    personalremarks: "Bought it from a friend. She just started an online roastery in Jakarta.",
    beanspicture: " https://storage.googleapis.com/erc-photos/coffee/home-brew/VIelGayo.jpg",
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 7.5, category: "Home Brew",
  },
  {
    beansName: "Lulalao Phonsavan, Xieng Khouang",
    roaster: "Lulalao Coffee, Laos",
    altitude: "1750",
    varietal: "Geisha",
    process: "Natural",
    tastingnotes: "",
    personalremarks: "Bought it from my trip to Laos. Not like south American gesha though haha.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/LaosLulalaoGesha.webp",
    originCountry: "Laos", roasterCountry: "Laos", rating: 7, category: "Home Brew",
  },
  {
    beansName: "LuLaLao Paksong Farm, Champasak, Laos",
    roaster: "Lulalao Coffee, Laos",
    altitude: "1200-1350",
    varietal: "Typica",
    process: "Anaerobic Natural",
    tastingnotes: "",
    personalremarks: "Bought it from my trip to Laos.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/LaosLulalaoAnaerobicNatural.webp",
    originCountry: "Laos", roasterCountry: "Laos", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Colombia Rio Negro, Huila",
    roaster: "Zero Coffee",
    altitude: "1750",
    varietal: "Pink Bourbon",
    process: "Coferment Orange",
    tastingnotes: "Pineapple, Lychee, Orange Zest",
    personalremarks: "Very sweet like fruit punch, bought it from Singapore Coffee Week 2025.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/ZeroCoffeeTropical.webp",
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 9, category: "Home Brew",
  },
  {
    beansName: "Semendo, South Sumatra Indonesia",
    roaster: "Rumah Sangrai Bloom, Jakarta, Indonesia",
    altitude: "1600-1650",
    varietal: "",
    process: "Natural",
    tastingnotes: "Orange, Grapes, Milk Chocolate",
    personalremarks: "Very sweet and aromatic.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/RumahSangraiBloomSemendo.jpeg",
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8, category: "Home Brew",
  },
  {
    beansName: "White Rabbit Espresso",
    roaster: "Rumah Sangrai Bloom, Jakarta, Indonesia",
    altitude: "1600-1650",
    varietal: "Mixed",
    process: "Natural",
    tastingnotes: "Soursop, Raspberry Candy, Strawberry Milk, Hint of Jackfruit",
    personalremarks: "I like the floral notes, very low bitterness, and sweet lingering finish.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/RumahSangraiBloomEspressoWhiteRabbitBlend.jpeg",
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8, category: "Home Brew",
  },
  {
    beansName: "ATY ZARKEIWIN",
    roaster: "People Coffee, Wellington, New Zealand",
    altitude: "",
    varietal: "Mixed",
    process: "",
    tastingnotes: "Orange, Peach, Red Apple, Grape",
    personalremarks: "My bro bought it from New Zealand.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/NewZealandPeopleCoffeeAty.webp",
    originCountry: "Unknown", roasterCountry: "New Zealand", rating: 5, category: "Home Brew",
  },
  {
    beansName: "West Java Wanoja, Indonesia",
    roaster: "Ultra Coffee, Surabaya, Indonesia",
    altitude: "1500",
    varietal: "Lini-S",
    process: "Carbonic Maceration Natural",
    tastingnotes: "Cherry, Tropical Fruits, Chocolate",
    personalremarks: "Bought it back from my World of Coffee 2025 Jakarta trip.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/UltraCoffeeJavaWanoja.jpg",
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Colombia Risaralda Finca Milan Logan #186",
    roaster: "Urban Coffee Roasters, Hong Kong",
    altitude: "1750",
    varietal: "Castillo and Caturra",
    process: "Carbonic Maceration with Lactobacillus Washed",
    tastingnotes: "Vanilla, Blackcurrant Candy, Mayonnaise",
    personalremarks: "Bought it from my Hong Kong conference trip, very sweet.",
    beanspicture: require('../../images/Coffee/Hongkong/UrbanFincaMilan.webp'),
    originCountry: "Colombia", roasterCountry: "Hong Kong", rating: 9, category: "Home Brew",
  },
  {
    beansName: "Gachala Blend",
    roaster: "Tiong Hoe, Singapore",
    altitude: "",
    varietal: "",
    process: "",
    tastingnotes: "Gula Melaka, Stone Fruits, Berries",
    personalremarks: "Good enough for espresso.",
    beanspicture: require('../../images/Coffee/TiongHoeHouseBlend.webp'),
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Temanggung, Central Java",
    roaster: "Strada Coffee, Indonesia",
    altitude: "1500-1600",
    varietal: "Kartika, Lini S, Typica",
    process: "Experimental",
    tastingnotes: "Peach, Bubblegum, Caramel",
    personalremarks: "Bought it during the 2025 Indonesia Barista/Brewer Championship exhibition, during my CNY trip.",
    beanspicture: require('../../images/Coffee/Indonesia/StradaJavaTemanggung.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 9, category: "Home Brew",
  },
  {
    beansName: "Moonstone, Chiang Mai Thailand",
    roaster: "Nana Coffee, Bangkok, Thailand",
    altitude: "1300",
    varietal: "Mixed",
    process: "Experimental multi-cultured inoculated yeast",
    tastingnotes: "Lychee, Rose, Candy",
    personalremarks: "Taste like peach/lychee, my bro brought the beans back from his trip to Thailand.",
    beanspicture: 'https://storage.googleapis.com/erc-photos/coffee/cupping/NanaCoffeeMoonstone.webp',
    originCountry: "Thailand", roasterCountry: "Thailand", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Yunnan Chiliarch Estate Ou Yang Natural, China",
    roaster: "Small Wave, Singapore",
    altitude: "900-1300",
    varietal: "Catimor",
    process: "Natural",
    tastingnotes: "Strawberry, Blackberry, Pine Nuts, Roasted Almonds, Pink Florals",
    personalremarks: "Bought it during Singapore Coffee Week 2024, very sweet.",
    beanspicture: require('../../images/Coffee/SmallWavesChinaYUnnan.webp'),
    originCountry: "China", roasterCountry: "Singapore", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "La Divisa Papayo Huila",
    roaster: "Parchmen Coffee, Singapore",
    altitude: "1850-2200",
    varietal: "Papayo",
    process: "Washed",
    tastingnotes: "Lime, Berries, Chrysanthemum",
    personalremarks: "Bought it at Singapore Coffee Week 2024, very bergamot.",
    beanspicture: require('../../images/Coffee/ParchmenPapayoLaDivisa.webp'),
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Yirgacheffe Konga Cooperative",
    roaster: "Single Profile Coffee",
    altitude: "1850-2200",
    varietal: "Heirloom",
    process: "Washed",
    tastingnotes: "Bergamot, Stone Fruit, Black Tea",
    personalremarks: "Bought it at Singapore Coffee Week 2024, very bergamot.",
    beanspicture: require('../../images/Coffee/SingleProfileCoffeeEthiopiaKonga.webp'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Suke Quto",
    roaster: "PPP Coffee, Singapore",
    altitude: "1850-2200",
    varietal: "Welicho and Kurume",
    process: "Washed",
    tastingnotes: "Peach, Earl Grey Tea, Citrus Finish",
    personalremarks: "",
    beanspicture: require('../../images/Coffee/PPPEthiopia-Suke-Quto.webp'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Tanamera Coffee Premium Blend",
    roaster: "Tanamera Coffee, Indonesia and Singapore",
    altitude: "",
    varietal: "Mixed",
    process: "Mixed",
    tastingnotes: "Sweet Fruits, Pear, Roasted Peanut, Lime Acidity, Pleasant Finish",
    personalremarks: "Place that I part-timed before. I like this espresso-based blend for its sweetness.",
    beanspicture: require('../../images/Coffee/TanameraCoffeePremiumBlend.webp'),
    originCountry: "Indonesia", roasterCountry: "Singapore", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Veracruz Santa Barbara",
    roaster: "Alchemist, Singapore",
    altitude: "",
    varietal: "Typica Catimor",
    process: "Extended Washed",
    tastingnotes: "Prune, Apricot Jam, Yellow Plum",
    personalremarks: "Needed a good espresso bean, and this shop is close to my home.",
    beanspicture: require('../../images/Coffee/AlchemistVeracruz.jpg'),
    originCountry: "Mexico", roasterCountry: "Singapore", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Jairo Arcila Santa Monica",
    roaster: "Parchmen Coffee, Singapore",
    altitude: "",
    varietal: "Typica Catimor",
    process: "Banana Maceration",
    tastingnotes: "Banana and White Chocolate",
    personalremarks: "Tastes as expected.",
    beanspicture: require('../../images/Coffee/ParchmenCoffeeColombiaBanana.webp'),
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Adena Kampung Kenawat Serinen Gayo",
    roaster: "People Temple Roastery, Indonesia",
    altitude: "1200-1800",
    varietal: "Typica Catimor",
    process: "Anaerobic Fermentation",
    tastingnotes: "Pineapple, Passionfruit, Sugarcane, Cacao Nibs",
    personalremarks: "Bought during my holiday in Indonesia. This is one of the top Indonesia CoE farms.",
    beanspicture: require('../../images/Coffee/Indonesia/PeopleTempleAcehKenawatAdena.jpg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Luis Andres Caqueta, Colombia",
    roaster: "Wolf Coffee, Queenstown, New Zealand",
    altitude: "1800",
    varietal: "Caturra, Castillo",
    process: "Anaerobic Fermentation",
    tastingnotes: "Mango, Passionfruit, Jasmine Honeysuckle, Maple Syrup",
    personalremarks: "Bought from my trip to New Zealand.",
    beanspicture: require('../../images/Coffee/NewZealand/WolfCoffeeNewZealandColombia.webp'),
    originCountry: "Colombia", roasterCountry: "New Zealand", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Aquiares Estate Esperanza, Turrialba, Costa Rica",
    roaster: "Alchemist Coffee Singapore",
    altitude: "",
    varietal: "Esperanza",
    process: "Natural",
    tastingnotes: "Cherry, Strawberry, Hibiscus",
    personalremarks: "",
    beanspicture: require('../../images/Coffee/AlchemistCostaRicaEsperanza.jpg'),
    originCountry: "Costa Rica", roasterCountry: "Singapore", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Riverdale Estate Peach, Shevaroy Hill, India",
    roaster: "Dutch Colony",
    altitude: "1450",
    varietal: "SL9",
    process: "Natural",
    tastingnotes: "Sultana Biscuit, Fig, Orange, Dried Apricot",
    personalremarks: "Buy 1 get 1 Dutch Colony birthday promo.",
    beanspicture: require('../../images/Coffee/DutchColonyIndia.webp'),
    originCountry: "India", roasterCountry: "Singapore", rating: 6.5, category: "Home Brew",
  },
  {
    beansName: "Cascarita Paraneima, Honduras",
    roaster: "Dutch Colony",
    altitude: "1210",
    varietal: "Paraneima",
    process: "Natural",
    tastingnotes: "Rose Apple, Limeade, Dried Apricot",
    personalremarks: "Buy 1 get 1 Dutch Colony birthday promo.",
    beanspicture: require('../../images/Coffee/DutchColonyHonduras.webp'),
    originCountry: "Honduras", roasterCountry: "Singapore", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Mesina Muje",
    roaster: "Homeground Coffee Singapore",
    altitude: "",
    varietal: "Heirloom",
    process: "Natural",
    tastingnotes: "Earl Grey Tea, Blueberry, Mango, Hints of Bergamot",
    personalremarks: "Free from Homeground Coffee cupping event hehe.",
    beanspicture: require('../../images/Coffee/HomegroundMesinaMuje.webp'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Finca La Veraneras, Colombia",
    roaster: "Hydrangea Coffee Roaster, USA",
    altitude: "1750",
    varietal: "Sudan Rume",
    process: "Natural",
    tastingnotes: "Green Apple, Blackberry, Kombu",
    personalremarks: "Bought this from a friend doing a monthly coffee subscription.",
    beanspicture: require('../../images/Coffee/USA/HydrangeaSudanRume.webp'),
    originCountry: "Colombia", roasterCountry: "USA", rating: 9, category: "Home Brew",
  },
  {
    beansName: "Pantan Musara Aceh Ribang Gayo",
    roaster: "Espresso Embassy Jakarta",
    altitude: "1600",
    varietal: "ATENG, BOURBON, P88",
    process: "Natural",
    tastingnotes: "Apple, Berries, Lychee, Mango, Melon, Orange, Pineapple, Milky Chocolate",
    personalremarks: "CoE #1 Coffee Indonesia 2022.",
    beanspicture: require('../../images/Coffee/Indonesia/EspressoEmbassyRibangGayo.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Tuang Coffee Flores",
    roaster: "Espresso Embassy Jakarta",
    altitude: "1600",
    varietal: "ATENG, BOURBON, P88",
    process: "Natural",
    tastingnotes: "Berry, Candy, Cocoa, Grape, Lychee, Stone Fruits, Strawberry",
    personalremarks: "Very sweet like strawberry.",
    beanspicture: require('../../images/Coffee/Indonesia/EspressoEmbassyTuangFlores.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Yirgacheffe",
    roaster: "Wells Cafe, Penang, Malaysia",
    altitude: "2300-2500",
    varietal: "Heirloom",
    process: "Double Anaerobic Washed",
    tastingnotes: "Rose, Mixed Berries, Peach, Citrus, Candy Mouthfeel",
    personalremarks: "Silky, simple sugar sweetness, floral. Bought this back from my Penang trip.",
    beanspicture: require('../../images/Coffee/Malaysia/WellsCafeMidsummer.jpeg'),
    originCountry: "Ethiopia", roasterCountry: "Malaysia", rating: 9, category: "Home Brew",
  },
  {
    beansName: "Finca La Virgen - Matagalpa, Nicaragua",
    roaster: "Parallel Coffee, Singapore",
    altitude: "950-1100",
    varietal: "Catuai, Bourbon, Caturra",
    process: "Natural",
    tastingnotes: "Chocolate, Caramel, Starfruit, Cacao Nibs",
    personalremarks: "Sweet & funky. Bought during 11.11 sale.",
    beanspicture: require('../../images/Coffee/ParallelFincaLaVirgen.jpeg'),
    originCountry: "Nicaragua", roasterCountry: "Singapore", rating: 8, category: "Home Brew",
  },
  {
    beansName: "El Triunfo Bloom Chiapas, Mexico",
    roaster: "Parallel Coffee, Singapore",
    altitude: "1250-1350",
    varietal: "Catuai, Bourbon, Caturra",
    process: "Washed",
    tastingnotes: "Cacao, Walnut, Green Apple, Caramel, Lemon",
    personalremarks: "11.11 Sale.",
    beanspicture: require('../../images/Coffee/ParallelElTriumfoBloom.jpeg'),
    originCountry: "Mexico", roasterCountry: "Singapore", rating: 7.5, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Sidamo Bensa",
    roaster: "Yasumi Coffee, Singapore",
    altitude: "2100",
    varietal: "Mixed Heirloom",
    process: "Natural",
    tastingnotes: "Blueberries, Chocolate",
    personalremarks: "New online roastery free trial 120g.",
    beanspicture: require('../../images/Coffee/YasumiEthiopiaV2.jpeg'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Aceh Gayo Central Sumatera Coffee",
    roaster: "Onelion Coffee, Singapore",
    altitude: "1400",
    varietal: "Bourbon/Abbhysinia/Ateng",
    process: "Anaerobic Natural",
    tastingnotes: "Passion Fruits, Dried Mango, Floral, Cascara Tea, Black Tea",
    personalremarks: "New online roastery started Sep 2023. Buy one get one promotion.",
    beanspicture: require('../../images/Coffee/OneLionGayoAnaerobic.webp'),
    originCountry: "Indonesia", roasterCountry: "Singapore", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Aceh Gayo Central Sumatera Coffee",
    roaster: "Onelion Coffee, Singapore",
    altitude: "1400",
    varietal: "Bourbon/Abbhysinia/Ateng",
    process: "Natural",
    tastingnotes: "Chocolate, Tea, Hint of Melon",
    personalremarks: "New online roastery started Sep 2023. Buy one get one promotion.",
    beanspicture: require('../../images/Coffee/OneLionGayoNatural.webp'),
    originCountry: "Indonesia", roasterCountry: "Singapore", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Panama Kambera Geisha",
    roaster: "Cumulo Coffee Singapore",
    altitude: "1200-1550",
    varietal: "Geisha",
    process: "Anaerobic Natural",
    tastingnotes: "Cherry, Tropical Fruits, Butterscotch, Rum Cake",
    personalremarks: "50 percent discount first time user.",
    beanspicture: require('../../images/Coffee/cumuloPanamaGeisha.webp'),
    originCountry: "Panama", roasterCountry: "Singapore", rating: 9, category: "Home Brew",
  },
  {
    beansName: "Colombia Yobani Ramos",
    roaster: "Morgon Coffee Roasters, Gothenburg, Sweden",
    altitude: "1900",
    varietal: "Pink Bourbon",
    process: "Washed",
    tastingnotes: "Juicy and clean with notes of lemonade and mango",
    personalremarks: "Beans I brought back from Sweden to Singapore.",
    beanspicture: require('../../images/Coffee/MorgonCoffeeColombia.jpeg'),
    originCountry: "Colombia", roasterCountry: "Sweden", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Diana & Hector Ovalle, Huehuetenango, Guatemala",
    roaster: "Andersen & Maillard, Denmark",
    altitude: "",
    varietal: "Caturra",
    process: "Natural",
    tastingnotes: "Strawberry, Raspberry, Peach, Rose",
    personalremarks: "The beans I brought back from Denmark Coffee Festival.",
    beanspicture: require('../../images/Coffee/Europe/AndersenMailllardGuatemalaDenmark.jpeg'),
    originCountry: "Guatemala", roasterCountry: "Denmark", rating: 9, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Shoondisha",
    roaster: "Manhattan Coffee Roasters, Netherlands",
    altitude: "",
    varietal: "74110, 74112",
    process: "Natural",
    tastingnotes: "Berry, Strawberry, White Sugar",
    personalremarks: "Generally fruity and sweet. Bought this at Arrenius Coffee Uppsala, Sweden.",
    beanspicture: require('../../images/Coffee/Europe/ManhattanEthipiaShoondisa.png'),
    originCountry: "Ethiopia", roasterCountry: "Netherlands", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Yirgacheffe",
    roaster: "Mok Coffee, Belgium",
    altitude: "",
    varietal: "Kurume",
    process: "Washed",
    tastingnotes: "Floral",
    personalremarks: "Trying to make espresso beans with hand brew. Not bad but not that special either.",
    beanspicture: require('../../images/Coffee/Europe/MokEthiopia.png'),
    originCountry: "Ethiopia", roasterCountry: "Belgium", rating: 7.5, category: "Home Brew",
  },
  {
    beansName: "Bali Kintamani Karana Madu, Catur Region",
    roaster: "Expat Roasters, Bali, Indonesia",
    altitude: "1800-2200",
    varietal: "USDAI",
    process: "Natural",
    tastingnotes: "Sweet Rockmelon, Peanut, Jackfruit, Hints of Dates, Biscuits",
    personalremarks: "Orange and honey/melon sweetness.",
    beanspicture: require('../../images/Coffee/Expat-KintamaniNatural.webp'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 7.5, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Yirgacheffe Gedeb",
    roaster: "Cumulo Coffee, Singapore",
    altitude: "1800-2200",
    varietal: "Heirloom",
    process: "Washed",
    tastingnotes: "Lemon Verbena, Tangerine, Lemon Grass, Black Tea, Citrus Blossom",
    personalremarks: "Lemon, black tea, and sometimes may have brown sugar finish.",
    beanspicture: require('../../images/Coffee/Cumulo-YirgacheffeGedeb.webp'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Gunung Baru, Aceh",
    roaster: "People Temple, Indonesia",
    altitude: "1200-1600",
    varietal: "Mixed",
    process: "Extended Natural",
    tastingnotes: "Grapefruit, Ripe Peach, Bittersweet Chocolate",
    personalremarks: "Strong lychee flavour, funky, the best Indo beans I've tried.",
    beanspicture: require('../../images/Coffee/PeopleTemple-AcehExtended.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Colombia Finca Villarazo",
    roaster: "Cumulo Coffee, Singapore",
    altitude: "1800",
    varietal: "Castillo",
    process: "Extended Natural, Ice Fermentation",
    tastingnotes: "Peach, Blackberry, Chocolate",
    personalremarks: "Pineapple, grape, honey.",
    beanspicture: require('../../images/Coffee/Cumulo-FincaVillarazo.webp'),
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 9, category: "Home Brew",
  },
  {
    beansName: "Flores Uwu #01",
    roaster: "Common Grounds, Indonesia",
    altitude: "1400-1600",
    varietal: "Kartika",
    process: "Anaerobic Natural",
    tastingnotes: "Blueberry, Grape Gum, Passion Fruit",
    personalremarks: "Berry notes.",
    beanspicture: require('../../images/Coffee/CommonGrounds-FloresUwu.png'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8.5, category: "Home Brew",
  },
  {
    beansName: "Bali Kintamani",
    roaster: "Common Grounds, Indonesia",
    altitude: "",
    varietal: "Lini S795 Kartika",
    process: "Natural",
    tastingnotes: "Mangoes, Banana, Brown Sugar",
    personalremarks: "The most prominent taste is banana.",
    beanspicture: require('../../images/Coffee/CommonGrounds-BaliKintamani.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Bali Kintamani 04",
    roaster: "Common Grounds, Indonesia",
    altitude: "1400-1450",
    varietal: "Kartika",
    process: "Experimental",
    tastingnotes: "Blackforest Cake, Cherry, Rum",
    personalremarks: "I don't really remember...",
    beanspicture: require('../../images/Coffee/CommonGrounds-BaliKintamani04.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Hambela Wamena",
    roaster: "Common Grounds, Indonesia",
    altitude: "1950-2000",
    varietal: "Wolisho Kurume, 74110, 74112",
    process: "Natural",
    tastingnotes: "Rambutan, Rose Tea, Rose Apple",
    personalremarks: "When I brewed it, tasted like ginger and Chinese herb.",
    beanspicture: require('../../images/Coffee/CommonGrounds-Ethiopia Hambela.jpeg'),
    originCountry: "Ethiopia", roasterCountry: "Indonesia", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Toraja Kalosi",
    roaster: "Common Grounds, Indonesia",
    altitude: "",
    varietal: "Lini-S",
    process: "Full Washed",
    tastingnotes: "Brown Sugar, Floral, Citrus",
    personalremarks: "Brown sugar notes, which is not common for washed coffee.",
    beanspicture: require('../../images/Coffee/CommonGrounds-TorajaKalosi.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 6, category: "Home Brew",
  },
  {
    beansName: "Nyamasheke, Lake Kivu, Rwanda",
    roaster: "Parallel Coffee, Singapore",
    altitude: "1700-2100",
    varietal: "Red Bourbon",
    process: "Natural",
    tastingnotes: "Strawberries, Dark Chocolate, Red Ripe Fruits",
    personalremarks: "I bought this with my friends because of 50% discount.",
    beanspicture: require('../../images/Coffee/ParallelLakeKivu.jpeg'),
    originCountry: "Rwanda", roasterCountry: "Singapore", rating: 7, category: "Home Brew",
  },
  {
    beansName: "San Isidro, Costa Rica",
    roaster: "Parallel Coffee, Singapore",
    altitude: "1350",
    varietal: "Caturra and Catuai",
    process: "Natural",
    tastingnotes: "Chocolate, Macerated Grapes, Raisins, Lime, Syrupy, Winey",
    personalremarks: "I bought this with my friends because of 50% discount.",
    beanspicture: require('../../images/Coffee/Parallel-SanIsidro.jpeg'),
    originCountry: "Costa Rica", roasterCountry: "Singapore", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Shaakkiso Daanisa",
    roaster: "Parallel Coffee, Singapore",
    altitude: "2000-2178",
    varietal: "Heirloom",
    process: "Natural",
    tastingnotes: "Floral, Candied Orange, Grapefruit, Pomegranate",
    personalremarks: "I bought this with my friends because of 50% discount.",
    beanspicture: require('../../images/Coffee/Parallel-Shaakiso.jpeg'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Mekarwangi, West Java",
    roaster: "Tadasih, Yogyakarta, Indonesia",
    altitude: "",
    varietal: "",
    process: "",
    tastingnotes: "Orange, Berries, Boozy, Sweet",
    personalremarks: "One day I want to sell coffee the way Tadasih does :)",
    beanspicture: require('../../images/Coffee/Tadasih-Mekarwangi.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Temanggung, Central Java",
    roaster: "Tadasih, Yogyakarta, Indonesia",
    altitude: "",
    varietal: "",
    process: "",
    tastingnotes: "Blueberry, Blackberry, Strawberry, Choco Cream",
    personalremarks: "One day I want to sell coffee the way Tadasih does :)",
    beanspicture: require('../../images/Coffee/Tadasih-Temanggung.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 7.5, category: "Home Brew",
  },
  {
    beansName: "Mount Halu, West Java",
    roaster: "Space Roastery, Yogyakarta, Indonesia",
    altitude: "",
    varietal: "",
    process: "Honey",
    tastingnotes: "Banana, Cotton Candy, Raw Honey, Orange",
    personalremarks: "",
    beanspicture: require('../../images/Coffee/SpaceRoastery HaluBanana.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Mount Halu, West Java",
    roaster: "Space Roastery, Yogyakarta, Indonesia",
    altitude: "",
    varietal: "",
    process: "Extended Yeast Fermentation",
    tastingnotes: "Red Wine, Ripe Apple, Bittersweet Caramel",
    personalremarks: "",
    beanspicture: require('../../images/Coffee/SpaceRoastery HaluBanana.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Ethiopia Yirgacheffe",
    roaster: "Perk Coffee, Singapore",
    altitude: "2000-2200",
    varietal: "Heirloom",
    process: "Natural",
    tastingnotes: "Lavender, Peach, Wild Berry",
    personalremarks: "So far the best one from Perk.",
    beanspicture: require('../../images/Coffee/Perk-Yirgacheffe.webp'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 8, category: "Home Brew",
  },
  {
    beansName: "Brazil Cerrado",
    roaster: "Perk Coffee, Singapore",
    altitude: "1000-1200",
    varietal: "Bourbon, Catuai",
    process: "Natural",
    tastingnotes: "Milk Chocolate, Caramel, Toasted Hazelnut",
    personalremarks: "",
    beanspicture: require('../../images/Coffee/Perk-Cerrado.webp'),
    originCountry: "Brazil", roasterCountry: "Singapore", rating: 6, category: "Home Brew",
  },
  {
    beansName: "Santa Rosa Guatemala",
    roaster: "Perk Coffee, Singapore",
    altitude: "1000-1200",
    varietal: "Catuai, Pache, Catimor",
    process: "Washed",
    tastingnotes: "Dark Cherry, Fig, Brown Sugar",
    personalremarks: "",
    beanspicture: require('../../images/Coffee/Perk-Guatemala.webp'),
    originCountry: "Guatemala", roasterCountry: "Singapore", rating: 6, category: "Home Brew",
  },
  {
    beansName: "Colombia Altura",
    roaster: "Perk Coffee, Singapore",
    altitude: "1400-1800",
    varietal: "Catuai, Pache, Catimor",
    process: "Washed",
    tastingnotes: "Pomegranate, Red Grape, Honey",
    personalremarks: "",
    beanspicture: require('../../images/Coffee/Perk-Colombia.webp'),
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 6, category: "Home Brew",
  },
  {
    beansName: "Espresso Blend",
    roaster: "Cowpresso Coffee Roasters, Singapore",
    altitude: "",
    varietal: "",
    process: "",
    tastingnotes: "Simple, fruity",
    personalremarks: "",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/cowpresso.webp",
    originCountry: "Unknown", roasterCountry: "Singapore", rating: 7, category: "Home Brew",
  },
  {
    beansName: "Mount Prau Black Honey, Central Java",
    roaster: "Space Roastery, Yogyakarta, Indonesia",
    altitude: "1400-1500",
    varietal: "SL792, Kartika, Typica",
    process: "Hybrid Honey Triple Yeast Mossto",
    tastingnotes: "",
    personalremarks: "Roast profile: best for filter brewing.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/SpaceRoasteryPrauBlackHoney.jpg",
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 8, category: "Home Brew",
  },

  // ─── Self Roasted ─────────────────────────────────────────────────────────
  {
    beansName: "Rwanda Nkora CWS, Western Province",
    roaster: "Myself with Ikawa Roaster",
    altitude: "",
    varietal: "Bourbon",
    process: "Washed",
    tastingnotes: "Apple and Hint of Chocolate",
    personalremarks: "Free from Singapore Coffee Association.",
    beanspicture: require('../../images/Coffee/OwnRoast-RwandaWashed.jpeg'),
    originCountry: "Rwanda", roasterCountry: "Singapore", rating: 7, category: "Self Roast",
  },
  {
    beansName: "Rwanda Gisanga, Southern Province",
    roaster: "Myself with Ikawa Roaster",
    altitude: "1550-1850",
    varietal: "Bourbon",
    process: "Anaerobic Natural",
    tastingnotes: "Strawberry and Sweet",
    personalremarks: "Free from Singapore Coffee Association.",
    beanspicture: require('../../images/Coffee/OwnRoast-RwandaAnaerobic.jpeg'),
    originCountry: "Rwanda", roasterCountry: "Singapore", rating: 8, category: "Self Roast",
  },
  {
    beansName: "Rwanda",
    roaster: "Myself with Ikawa Roaster",
    altitude: "1550-1850",
    varietal: "Bourbon",
    process: "Honey",
    tastingnotes: "Brown Sugar and Plum",
    personalremarks: "Free from Singapore Coffee Association.",
    beanspicture: require('../../images/Coffee/OwnRoast-RwandaHoney.jpeg'),
    originCountry: "Rwanda", roasterCountry: "Singapore", rating: 7, category: "Self Roast",
  },
  {
    beansName: "Rwanda Gisanga, Southern Province",
    roaster: "Myself with Ikawa Roaster",
    altitude: "1930-2200",
    varietal: "Bourbon",
    process: "Natural",
    tastingnotes: "Cherry",
    personalremarks: "Free from Singapore Coffee Association.",
    beanspicture: require('../../images/Coffee/OwnRoast-RwandaNatural.jpeg'),
    originCountry: "Rwanda", roasterCountry: "Singapore", rating: 7, category: "Self Roast",
  },
  {
    beansName: "Rwanda",
    roaster: "Myself with Ikawa Roaster",
    altitude: "",
    varietal: "Bourbon",
    process: "Washed",
    tastingnotes: "Hint of Citrus",
    personalremarks: "Free from Singapore Coffee Association.",
    beanspicture: require('../../images/Coffee/OwnRoast-Rwanda-Fullwashed2.jpeg'),
    originCountry: "Rwanda", roasterCountry: "Singapore", rating: 7, category: "Self Roast",
  },
  {
    beansName: "Lot 37 Rwanda, Juru Coffee Farm",
    roaster: "Myself with Ikawa Roaster",
    altitude: "1700-2200",
    varietal: "Red Bourbon",
    process: "Natural",
    tastingnotes: "Jasmine and Peach",
    personalremarks: "Free from Visit Rwanda Committee 2022.",
    beanspicture: require('../../images/Coffee/OwnRoast-RwandaLot37.jpg'),
    originCountry: "Rwanda", roasterCountry: "Singapore", rating: 7, category: "Self Roast",
  },
  {
    beansName: "Costa Rica Aguilera Brothers",
    roaster: "Myself",
    altitude: "1800-2200",
    varietal: "San Roque Kenia",
    process: "Anaerobico",
    tastingnotes: "Cherry, Black Tea",
    personalremarks: "Free samples from Compound Coffee.",
    beanspicture: require('../../images/Coffee/OwnRoast-CostaRicaAnaerobico.jpg'),
    originCountry: "Costa Rica", roasterCountry: "Singapore", rating: 7, category: "Self Roast",
  },
  {
    beansName: "Klasik Beans Mt Puntang West Java Aromanis",
    roaster: "Klasik Beans",
    altitude: "1269",
    varietal: "Typica, Lini S, Katimor",
    process: "Natural",
    tastingnotes: "Sweet, Fruity, Chocolatey",
    personalremarks: "I visited the farm in September, bought some roasted beans as well as green beans to roast at home.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/KLASIKBEANSsundaaromanis.jpg",
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 7.5, category: "Self Roast",
  },
  {
    beansName: "Klasik Beans Mt Puntang West Java Gulali",
    roaster: "Klasik Beans",
    altitude: "1269",
    varietal: "Typica, Lini S, Katimor",
    process: "Honey",
    tastingnotes: "Brown Sugar, Caramel, Fruity",
    personalremarks: "I visited the farm in September, bought some roasted beans as well as green beans to roast at home.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/home-brew/KlasikBeansSundaGUlali.jpg",
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 7.5, category: "Self Roast",
  },
  {
    beansName: "Karana Kintamanis",
    roaster: "Myself",
    altitude: "1200-1400",
    varietal: "S795-Kartika",
    process: "Natural",
    tastingnotes: "Berries, Caramel Finish",
    personalremarks: "I went to Karana Coffee Processing Facility in Bali, got the green beans, and roasted them with my friend in Singapore.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/roasting/karanaKintamanis.webp",
    originCountry: "Indonesia", roasterCountry: "Singapore", rating: 7.5, category: "Self Roast",
  },
  {
    beansName: "Menglian, Yunnan 26",
    roaster: "Zegnine Coffee, China",
    altitude: "1200-1400",
    varietal: "Menglian",
    process: "Anaerobic Natural",
    tastingnotes: "Fruity, Floral, Peach",
    personalremarks: "Free green beans samples from Zegnine Coffee, China. I happened to visit an exhibition in Shanghai, and they gave out some green beans for free. Very sweet peach notes.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/roasting/ZegnineYunnanHotelexShanghai2026.jpg",
    originCountry: "China", roasterCountry: "China", rating: 8.5, category: "Self Roast",
  },

  // ─── Cafe Visit / Cupping ─────────────────────────────────────────────────
  {
    beansName: "Carlos Vergara, Chiroso Magic",
    roaster: "Tanat (Kawa) Roastery, France",
    altitude: "",
    varietal: "Chiroso",
    process: "Thermal Shock Washed",
    tastingnotes: "Vanilla, Raspberry, Violet",
    personalremarks: "Nicest beans from SG Coffee Neighbourhood public cupping session June 2025.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/SGCN_chiroso.jpeg",
    originCountry: "Colombia", roasterCountry: "France", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Colombia El Encanto",
    roaster: "Skill Issue Coffee, Singapore",
    altitude: "1700",
    varietal: "Papayo",
    process: "Multiferment Washed",
    tastingnotes: "Blueberry, Concord Grapes, Pink Guava",
    personalremarks: "Very sweet blueberry-like beans from SG Coffee Neighbourhood public cupping session June 2025.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/SGCN_papayo.jpeg",
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Yunnan People Temple Sidra",
    roaster: "People Temple, Jakarta, Indonesia",
    altitude: "",
    varietal: "Catimor",
    process: "Anaerobic Yeast Natural",
    tastingnotes: "Pomegranate, Rose, Cranberry",
    personalremarks: "Very sweet and floral like rose and peach, tried during World of Coffee 2025 Jakarta.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/PeopleTempleYunnan.jpg",
    originCountry: "China", roasterCountry: "Indonesia", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Las Flores Huila Colombia",
    roaster: "Miss Coco",
    altitude: "1700-1780",
    varietal: "Sidra",
    process: "Anaerobic Washed",
    tastingnotes: "Lychee, Molasses, Orange, Lemongrass & Rose Honey",
    personalremarks: "Very sweet lychee, tried during World of Coffee 2025 Jakarta.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/MrWushWushsidraBourbon.jpeg",
    originCountry: "Colombia", roasterCountry: "Indonesia", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Edinson Argote, Tipica Mejorado Huila Colombia",
    roaster: "Mr Wush Wush",
    altitude: "1780-1900",
    varietal: "Typica Mejorado",
    process: "Washed",
    tastingnotes: "Vanilla, Pineapple, Caramel, Miel",
    personalremarks: "Tastes like corn sweetness & umami, tried during World of Coffee 2025 Jakarta.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/MrWushWushTypicaMezorado.jpeg",
    originCountry: "Colombia", roasterCountry: "Colombia", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Edinson Argote, Papayo Huila Colombia",
    roaster: "Mr Wush Wush",
    altitude: "1780-1900",
    varietal: "Papayo",
    process: "Natural",
    tastingnotes: "Banano, Caramelo, Chocolate, Mango",
    personalremarks: "Very sweet like mango, tried during World of Coffee 2025 Jakarta.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/MRWUSHWUSHPAPAYO.webp",
    originCountry: "Colombia", roasterCountry: "Colombia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Las Flores Ombligon Jhoan Vergara",
    roaster: "Mr Wush Wush",
    altitude: "1780-1900",
    varietal: "Ombligon",
    process: "Natural",
    tastingnotes: "Blueberries, White Chocolate, Nutmeg, Vanilla",
    personalremarks: "Very sweet like blueberry gum, tried during World of Coffee 2025 Jakarta.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/MRWUSHWUSHombligon.webp",
    originCountry: "Colombia", roasterCountry: "Colombia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Pacamara Cold Brew",
    roaster: "Coffee Myungga, Korea",
    altitude: "1780-1900",
    varietal: "Pacamara",
    process: "-",
    tastingnotes: "Strawberry, Raspberry, Sweet, Fruity and Complex",
    personalremarks: "Very sweet cold brew like strawberry, tried during World of Coffee 2025 Jakarta.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/CoffeeMyunggaPacamara.jpg",
    originCountry: "Unknown", roasterCountry: "South Korea", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Nestor Lasso, Huila, Colombia",
    roaster: "Lohas Beans",
    altitude: "1780-1900",
    varietal: "Aji Bourbon",
    process: "Natural",
    tastingnotes: "Floral, Lavender, Passion Fruit, Apricot, Citrus, Slight Spice",
    personalremarks: "Tastes damn sweet and tropical, tried during World of Coffee 2025 Jakarta.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/LohasBeansAjiBourbon.jpeg",
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Jhoan Vergara, Huila, Colombia",
    roaster: "Lohas Beans",
    altitude: "1780-1900",
    varietal: "Chiroso",
    process: "Hydrohoney",
    tastingnotes: "Floral, Rose, Strawberries, Cherry, Bright Acidity and Sweet",
    personalremarks: "Tastes damn sweet like gum, tried during World of Coffee 2025 Jakarta.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/LohasChiroso.jpeg",
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Janson Lot 411, Panama",
    roaster: "The Kopi Times, Singapore",
    altitude: "",
    varietal: "Gesha",
    process: "72H Overripe Washed",
    tastingnotes: "Mandarin, Jasmine, Green Tea",
    personalremarks: "Really tastes like jasmine, from SG Coffee Neighbourhood public cupping session March 2025.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/SGCN_jansonJasmine.jpg",
    originCountry: "Panama", roasterCountry: "Singapore", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Alo Coffee Natural G1",
    roaster: "Tiong Hoe, Singapore",
    altitude: "1850",
    varietal: "74158",
    process: "96h Natural Fermentation",
    tastingnotes: "Mangoes, Muscat Grapes, Mulberries",
    personalremarks: "From SG Coffee Neighborhood Cupping, damn nice also.",
    beanspicture: require('../../images/Coffee/TIongHoeAloCoffee.webp'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Edison Argote Finca Quebraditas",
    roaster: "Crankhouse Coffee, UK",
    altitude: "1850",
    varietal: "Sidra",
    process: "Anaerobic Washed Inoculation",
    tastingnotes: "Pink Lemonade, Honeysuckle, Turkish Delight",
    personalremarks: "From SG Coffee Neighborhood Cupping, damn lychee the taste.",
    beanspicture: require('../../images/Coffee/Europe/CrankhouseUKSidraEdison.jpeg'),
    originCountry: "Colombia", roasterCountry: "UK", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Isabel Lopez Hernandez, Mexico",
    roaster: "Falcon Coffee Exporter",
    altitude: "",
    varietal: "Marsellesa",
    process: "Black Honey",
    tastingnotes: "",
    personalremarks: "From Beanvoyage x Falcon x Community Coffee, damn sweet for honey.",
    beanspicture: require('../../images/Coffee/MexicoIsabelLopezFalconBeanVoyage.jpeg'),
    originCountry: "Mexico", roasterCountry: "Unknown", rating: 7.5, category: "Cafe Visit",
  },
  {
    beansName: "Giant Pacamara",
    roaster: "Forest Green Coffee, Colombia",
    altitude: "1450",
    varietal: "Pacamara",
    process: "Natural Anaerobic",
    tastingnotes: "Fruity and spicy profile with yellow fruits, Melon, and nutty flavors, creamy body",
    personalremarks: "The beans are really damn big haha.",
    beanspicture: require('../../images/Coffee/Colombia/ForestGreenGiantPacamara.webp'),
    originCountry: "Colombia", roasterCountry: "Colombia", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Fellows Exotic",
    roaster: "Forest Green Coffee, Colombia",
    altitude: "1700-2000",
    varietal: "Pink Bourbon",
    process: "Natural Anaerobic",
    tastingnotes: "Floral, Tropical Fruits, Honey, Citric Acid, Lemon & Lime",
    personalremarks: "From a cupping session with Compound Coffee.",
    beanspicture: require('../../images/Coffee/Colombia/ForestGreenFellowsexoticssaco.webp'),
    originCountry: "Colombia", roasterCountry: "Colombia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Aji Bourbon",
    roaster: "Forest Green Coffee, Colombia",
    altitude: "1550",
    varietal: "Aji Bourbon",
    process: "Natural",
    tastingnotes: "Sweet and Umami",
    personalremarks: "From a cupping session with Compound Coffee.",
    beanspicture: require('../../images/Coffee/Colombia/ForestGreenAjiBorbon.webp'),
    originCountry: "Colombia", roasterCountry: "Colombia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Chinese New Year Blend 2024",
    roaster: "Dutch Colony Singapore",
    altitude: "1200",
    varietal: "",
    process: "Washed",
    tastingnotes: "Marmalade, Blood Orange, Lemon Loaf",
    personalremarks: "Really tastes like orange.",
    beanspicture: require('../../images/Coffee/DutchColonyCNY2024Coffee.webp'),
    originCountry: "Unknown", roasterCountry: "Singapore", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Pacamara Jonathan Gasca, Huila, Colombia",
    roaster: "Lohas Beans",
    altitude: "1550-1800",
    varietal: "Pacamara",
    process: "Washed",
    tastingnotes: "Mango, Starfruit",
    personalremarks: "Tastes damn sweet like mango, very rare for washed coffee to be this sweet. Tried during Lohas Beans x Omaafe Free Cupping Session.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/Lohas2024_HUilawashed.jpg",
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Pacamara Jonathan Gasca, Huila, Colombia",
    roaster: "Lohas Beans",
    altitude: "1550-1800",
    varietal: "Pacamara",
    process: "Natural",
    tastingnotes: "Floral and aromatic, tropical fruit, kiwi, pineapple, brilliant acidity, juicy and long residual",
    personalremarks: "Tastes damn sweet. Tried during Lohas Beans x Omaafe Free Cupping Session.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/3Pacamara_Tropical.webp.png",
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Adrian Lasso, Colombia",
    roaster: "Lohas Beans",
    altitude: "1780-1900",
    varietal: "Sidra",
    process: "Natural",
    tastingnotes: "Floral, Lemongrass, Jasmine, Cherries, Berries, Blood Orange, Juicy, Creamy Body",
    personalremarks: "Tastes damn sweet like blueberry. Tried during Lohas Beans x Omaafe Free Cupping Session.",
    beanspicture: "https://storage.googleapis.com/erc-photos/coffee/cupping/LohasBeansSidra.jpeg",
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 10, category: "Cafe Visit",
  },
  {
    beansName: "Taiwan COE#16 Melastoma Coffee Estate, Chiayi, Alishan",
    roaster: "Wells Cafe, Penang, Malaysia",
    altitude: "1200",
    varietal: "SL34",
    process: "Washed",
    tastingnotes: "Jasmine, Bergamot, Butterscotch, Sweet Plum, Green Apple",
    personalremarks: "Not exactly special taste-wise, but interesting to know there are specialty beans from Taiwan.",
    beanspicture: require('../../images/Coffee/Malaysia/WellsCafeTaiwanCoe16.png'),
    originCountry: "Taiwan", roasterCountry: "Malaysia", rating: 8, category: "Cafe Visit",
  },
  {
    beansName: "Kenya Karimikui",
    roaster: "Narrative Coffee, Singapore",
    altitude: "1650",
    varietal: "SL28, SL34, Ruiru 11",
    process: "Washed",
    tastingnotes: "Blackberry, Black Cherry, Roselle",
    personalremarks: "Sidra damn sweet as expected, with strong blueberry notes.",
    beanspicture: require('../../images/Coffee/NarrativeKenyaFilter.webp'),
    originCountry: "Kenya", roasterCountry: "Singapore", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Sidra Bourbon",
    roaster: "Asylum, Singapore",
    altitude: "1800",
    varietal: "Sidra",
    process: "Natural",
    tastingnotes: "Raisins, Blackberries, Molasses, Lavender",
    personalremarks: "Sidra damn sweet as expected, with strong blueberry notes.",
    beanspicture: require('../../images/Coffee/AsylumSidra.jpg'),
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Rafael Vinhal",
    roaster: "Five Senses, Australia",
    altitude: "980",
    varietal: "Catuai 144",
    process: "Double Fermented Washed",
    tastingnotes: "Coconut",
    personalremarks: "Tastes like coconut in a milk-based espresso, quite unique.",
    beanspicture: require('../../images/Coffee/Australia/FiveSensesRafaelVinhalBrazil.png'),
    originCountry: "Brazil", roasterCountry: "Australia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Kamala Batukaang",
    roaster: "Homeground Coffee, Singapore",
    altitude: "",
    varietal: "Lini-S, Kopyol, Sigararutang",
    process: "Washed Anaerobic + Natural Anaerobic",
    tastingnotes: "Dried Cranberry, Dried Apricot, Lychee, Plum, Cocoa Nibs",
    personalremarks: "",
    beanspicture: require('../../images/Coffee/HomegroundKamalaBatukaang.webp'),
    originCountry: "Indonesia", roasterCountry: "Singapore", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Bishala Guji Ethiopia",
    roaster: "Homeground Coffee, Singapore",
    altitude: "",
    varietal: "Heirloom",
    process: "Washed",
    tastingnotes: "White Flower, White Tea, Nectarine, Pear",
    personalremarks: "Very floral.",
    beanspicture: require('../../images/Coffee/HomegroundEthiopiaGuji.jpg'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Aceh Gayo Natural",
    roaster: "Tanamera Coffee Indonesia/Singapore",
    altitude: "",
    varietal: "",
    process: "Natural",
    tastingnotes: "Strawberry, Mirabelle, Black Tea",
    personalremarks: "Unlike other Aceh Gayo beans, this one is very sweet with strawberry notes. My favourite beans from my part-time job here.",
    beanspicture: require('../../images/Coffee/Tanamera_AcehGayo.webp'),
    originCountry: "Indonesia", roasterCountry: "Singapore", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Colombia Huila Jonathan Gasca",
    roaster: "Mok Coffee, Belgium",
    altitude: "",
    varietal: "Gesha",
    process: "Washed",
    tastingnotes: "Chamomile, Mirabelle, Black Tea",
    personalremarks: "Very floral, with pear sweetness notes as well.",
    beanspicture: require('../../images/Coffee/Europe/MokColombiaJonathanGasca.png'),
    originCountry: "Colombia", roasterCountry: "Belgium", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Guatemala",
    roaster: "Vero Cafe, Lithuania",
    altitude: "1700",
    varietal: "",
    process: "Anaerobic Natural",
    tastingnotes: "Grapefruit, Cocoa and Almond",
    personalremarks: "Didn't expect this Lithuanian coffee chain to have such a good cup of americano, with blueberry sweetness comparable to a good filter coffee.",
    beanspicture: require('../../images/Coffee/Europe/VeroCafeGuatemala.jpeg'),
    originCountry: "Guatemala", roasterCountry: "Lithuania", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Shwe Taung Thu, Myin Kya Doe, Myanmar",
    roaster: "The Miners, Czech Republic",
    altitude: "1300",
    varietal: "Red Catuai",
    process: "Natural",
    tastingnotes: "Mandarin, Cranberry",
    personalremarks: "Didn't expect Myanmar beans to be this sweet like orange.",
    beanspicture: require('../../images/Coffee/Europe/TheMinersMyanmarBeans.jpeg'),
    originCountry: "Myanmar", roasterCountry: "Czech Republic", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Colombia Alex Bermudez",
    roaster: "Good Coffee Micro Roasters, Poland",
    altitude: "1920",
    varietal: "Castilla",
    process: "Washed",
    tastingnotes: "The mango peach notes are very strong, it's like drinking Sjora",
    personalremarks: "Mango.",
    beanspicture: require('../../images/Coffee/Europe/GoodCoffeePoland.jpeg'),
    originCountry: "Colombia", roasterCountry: "Poland", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Nestor Lasso, El Diviso, Colombia",
    roaster: "Lehmus Roastery, Finland",
    altitude: "",
    varietal: "Red Bourbon",
    process: "Anaerobic Natural",
    tastingnotes: "",
    personalremarks: "Tastes like marshmallow, the best one at Helsinki Coffee Fest.",
    beanspicture: require('../../images/Coffee/Europe/LehmusColombiaFinlandCoffeeFest.png'),
    originCountry: "Colombia", roasterCountry: "Finland", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Burundi Nkonge",
    roaster: "Johan Nystrom, Sweden",
    altitude: "",
    varietal: "Heirloom",
    process: "Natural",
    tastingnotes: "Pomegranate, Passion Fruit, Yellow Rum",
    personalremarks: "From Finland Coffee Fest.",
    beanspicture: require('../../images/Coffee/Europe/JOhanNystromBurundi.jpeg'),
    originCountry: "Burundi", roasterCountry: "Sweden", rating: 8, category: "Cafe Visit",
  },
  {
    beansName: "Hacienda La Papaya, Juan Pena, Saraguro, Ecuador",
    roaster: "La Cabra",
    altitude: "2000",
    varietal: "Typica",
    process: "Anaerobic Honey",
    tastingnotes: "Floral and Wild",
    personalremarks: "From Danish Coffee Fest.",
    beanspicture: require('../../images/Coffee/Europe/LaCabraHaciendaLaPapaya.webp'),
    originCountry: "Ecuador", roasterCountry: "Denmark", rating: 9.5, category: "Cafe Visit",
  },
  {
    beansName: "Santa Teresa Geisha, Peru",
    roaster: "Prolog, Denmark",
    altitude: "1700",
    varietal: "Geisha",
    process: "Natural",
    tastingnotes: "Complex, Tutti Fruity, Raspberry",
    personalremarks: "From Danish Coffee Fest. Tastes like a fruit bomb.",
    beanspicture: require('../../images/Coffee/Europe/PrologSantaTeresaGeisha.png'),
    originCountry: "Peru", roasterCountry: "Denmark", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Finca Deborah Afterglow, Panama",
    roaster: "Amokka Nordic Roasters",
    altitude: "1700",
    varietal: "Geisha",
    process: "Carbonic Maceration",
    tastingnotes: "Big florality, striking notes of red fruit, nectarines and toffee with a juicy, syrupy body",
    personalremarks: "From Danish Coffee Fest. Tastes like a fruit bomb.",
    beanspicture: require('../../images/Coffee/Europe/AmokkaFincaDeborahAfterglow.webp'),
    originCountry: "Panama", roasterCountry: "Denmark", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Kenya Muchagara AA",
    roaster: "Koppi, Sweden",
    altitude: "1700",
    varietal: "SL34, SL28",
    process: "Washed",
    tastingnotes: "Forest Berries, Orange Blossoms, Honey",
    personalremarks: "From Danish Coffee Fest. Tastes like a fruit bomb.",
    beanspicture: require('../../images/Coffee/Europe/KoppiSwedenKenya.png'),
    originCountry: "Kenya", roasterCountry: "Sweden", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Jaime Geisha Huila Colombia",
    roaster: "Coffee Collective, Denmark",
    altitude: "1700",
    varietal: "Geisha",
    process: "Natural",
    tastingnotes: "Super balanced, floral and elegant coffee with a delicate acidity and sweetness",
    personalremarks: "From Danish Coffee Fest. Tastes very complex, floral, and bergamot.",
    beanspicture: require('../../images/Coffee/Europe/CoffeeCollectiveJaimeGeisha.png'),
    originCountry: "Colombia", roasterCountry: "Denmark", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Ethiopia Guji",
    roaster: "Beanbros, Denmark",
    altitude: "2000",
    varietal: "Heirloom",
    process: "Washed",
    tastingnotes: "Earl Grey, Lemon, Peach",
    personalremarks: "From Danish Coffee Fest.",
    beanspicture: require('../../images/Coffee/Europe/BeansBrosEthiopiaGuji.webp'),
    originCountry: "Ethiopia", roasterCountry: "Denmark", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Al Chirripo Costa Rica",
    roaster: "Jonas Reindl, Austria",
    altitude: "1750",
    varietal: "Caturra",
    process: "Anaerobic Natural",
    tastingnotes: "Wildflower Honey, Raspberry, Orange Wine, Juicy",
    personalremarks: "Nice fruity funky.",
    beanspicture: require('../../images/Coffee/Europe/JonasReindlAlChirripo.jpeg'),
    originCountry: "Costa Rica", roasterCountry: "Austria", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Ixhuatlan Mexico",
    roaster: "Compound Coffee, Singapore",
    altitude: "1500-1700",
    varietal: "Red Bourbon",
    process: "Anaerobic Natural",
    tastingnotes: "Green Grape, Merlot, Plum",
    personalremarks: "Tastes like green grape/white wine.",
    beanspicture: require('../../images/Coffee/CompoundIxhuatlan.jpeg'),
    originCountry: "Mexico", roasterCountry: "Singapore", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Ethiopia Yirgacheffe",
    roaster: "Say Something Coffee, Jakarta",
    altitude: "1500-1700",
    varietal: "Heirloom",
    process: "Washed",
    tastingnotes: "Watermelon, Blueberry, Black Tea",
    personalremarks: "Watermelon on point.",
    beanspicture: require('../../images/Coffee/SaySomethingEthiopiaYirgacheffe.jpeg'),
    originCountry: "Ethiopia", roasterCountry: "Indonesia", rating: 8, category: "Cafe Visit",
  },
  {
    beansName: "Ethiopia Gersay Beyou",
    roaster: "Alchemist, Singapore",
    altitude: "1500-1700",
    varietal: "Heirloom",
    process: "Washed",
    tastingnotes: "Lilac, Floral",
    personalremarks: "Strong floral lavender taste.",
    beanspicture: require('../../images/Coffee/AlchemistGersayBeyou.jpeg'),
    originCountry: "Ethiopia", roasterCountry: "Singapore", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Finca El Diviso, Colombia",
    roaster: "Placebo, Indonesia",
    altitude: "1700-1800",
    varietal: "Sidra",
    process: "Anaerobic Natural",
    tastingnotes: "Cherry, Blackberry, Red Wine, Purple Grape",
    personalremarks: "Free Exotic Cupping Session with Placebo X SMITH Jakarta. Sidra is in my top two alongside Eugenoides.",
    beanspicture: require('../../images/Coffee/PlaceboFincaLaDivisoSidra.jpeg'),
    originCountry: "Colombia", roasterCountry: "Indonesia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Inmaculada Coffee Farm, Colombia",
    roaster: "Placebo, Indonesia",
    altitude: "1700-2000",
    varietal: "Eugenoides",
    process: "Carbonic Maceration Natural",
    tastingnotes: "Pink Grapefruit, Kiwi, Rose, Apricot",
    personalremarks: "Free Exotic Cupping Session with Placebo X SMITH Jakarta. Eugenoides is too expensive to try for free hahaha.",
    beanspicture: require('../../images/Coffee/PlaceboInmaculadaEugenoides.jpeg'),
    originCountry: "Colombia", roasterCountry: "Indonesia", rating: 10, category: "Cafe Visit",
  },
  {
    beansName: "Finca Soledad, Ecuador",
    roaster: "Placebo, Indonesia",
    altitude: "1515",
    varietal: "Sidra",
    process: "Wave Natural + Mossto",
    tastingnotes: "Strawberry, Cranberries, Red Apple",
    personalremarks: "Free Exotic Cupping Session with Placebo X SMITH Jakarta. Sidra is in my top two alongside Eugenoides.",
    beanspicture: require('../../images/Coffee/PlaceboFincaSoledadSidra.jpeg'),
    originCountry: "Ecuador", roasterCountry: "Indonesia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Abu Coffee",
    roaster: "Placebo, Indonesia",
    altitude: "1700",
    varietal: "Geisha",
    process: "Natural",
    tastingnotes: "Raspberry Compote, Red Grape, Vanilla",
    personalremarks: "Free Exotic Cupping Session with Placebo X SMITH Jakarta.",
    beanspicture: require('../../images/Coffee/PlaceboAbuGesha.jpeg'),
    originCountry: "Unknown", roasterCountry: "Indonesia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "La Llama Bolivia",
    roaster: "Market Lane Coffee",
    altitude: "",
    varietal: "",
    process: "Washed",
    tastingnotes: "Red Apple, Sweet Nectarine",
    personalremarks: "Tastes exactly as the tasting notes.",
    beanspicture: require('../../images/Coffee/MarketLaneBolivia.webp'),
    originCountry: "Bolivia", roasterCountry: "Australia", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Colombia Castillo Jairo Arcilla Strawberry",
    roaster: "Parchmen Coffee, Singapore",
    altitude: "",
    varietal: "Pink Bourbon",
    process: "Strawberry Yeast",
    tastingnotes: "Strawberry",
    personalremarks: "Tried this during Singapore Coffee Fest 2022. The strawberry is too strong, hiding the coffee taste.",
    beanspicture: require('../../images/Coffee/ParchmenStrawberry.png'),
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 8.5, category: "Cafe Visit",
  },
  {
    beansName: "Colombia Los Tres Mosqueteros",
    roaster: "Dutch Colony, Singapore",
    altitude: "",
    varietal: "",
    process: "Washed",
    tastingnotes: "Yellow Peach, Lychee, Jasmine",
    personalremarks: "Tastes like lychee, pear, very sweet, floral.",
    beanspicture: require('../../images/Coffee/DutchColonyLostresMoquenteros.jpeg'),
    originCountry: "Colombia", roasterCountry: "Singapore", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Kenya AB",
    roaster: "Sweet Blossom, Johor Bahru, Malaysia",
    altitude: "",
    varietal: "SL28, SL34",
    process: "Washed",
    tastingnotes: "Prune, Blackcurrant, Herb, Chamomile",
    personalremarks: "During my first visit to Johor Bahru, Malaysia.",
    beanspicture: require('../../images/Coffee/SweetBlossomJohor.jpeg'),
    originCountry: "Kenya", roasterCountry: "Malaysia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Flores Mbohang Lot 347",
    roaster: "Tuang Coffee X Andrew Roastwork, Jakarta",
    altitude: "",
    varietal: "Kartika",
    process: "Anaerobic Natural",
    tastingnotes: "Sugar, Red Berries, Apricot, Grapes, Black Tea",
    personalremarks: "My top two from the Flores Coffee Cupping Session.",
    beanspicture: require('../../images/Coffee/TuangCoffeeMbohang347.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Flores Uwu Lot 812",
    roaster: "Tuang Coffee X Andrew Roastwork, Jakarta",
    altitude: "",
    varietal: "Kartika",
    process: "Anaerobic Natural",
    tastingnotes: "Panela, Red Fruits, Berries, Apricots, Grapes",
    personalremarks: "My top two from the Flores Coffee Cupping Session.",
    beanspicture: require('../../images/Coffee/TuangFloresUwu.jpeg'),
    originCountry: "Indonesia", roasterCountry: "Indonesia", rating: 9, category: "Cafe Visit",
  },
  {
    beansName: "Ethiopia Balesi Dingu",
    roaster: "Andrew Roastwork, Jakarta",
    altitude: "1750",
    varietal: "Heirloom",
    process: "Natural",
    tastingnotes: "Orange Blossom, Mango, Bergamot",
    personalremarks: "Strong bergamot notes.",
    beanspicture: require('../../images/Coffee/AndrewRoastEthiopiaBalesi.jpeg'),
    originCountry: "Ethiopia", roasterCountry: "Indonesia", rating: 8.5, category: "Cafe Visit",
  },
];
